import type {IAttributes, INgModelController, IParseService, IScope} from 'angular';

/**
 * @ngdoc directive
 * @name kpYearValidation
 * @module portaro.directives
 *
 * @requires ngModel
 *
 * @restrict A
 *
 * @param {boolean} kpYearValidation Enable/disable all validation. Default is true.
 * @param {number} minYear Min year value.
 * @param {number} maxYear Max year value.
 *
 * @description
 * Validation of min and max year.
 * If `minYear` or `maxYear` is not defined, responsible validation is disabled.
 * You can enable / disable entire validation by `kpYearValidation` attribute.
 *
 * @example
 * ```
 * <example module="kpYearValidationExample" name="kpYearValidationExample" frame-no-resize="true">
 *     <file name="index.html">
 *         <main ng-form="form" ng-init="enableValidation = true;">
 *             <label for="dateInput">Date: </label>
 *             <input type="text" id="dateInput" name="date" ng-model="date" kp-year-validation="enableValidation" min-year="minYear" max-year="maxYear">
 *             <span class="label label-danger" ng-if="form.date.$error.minYear">Invalid min year</span>
 *             <span class="label label-danger" ng-if="form.date.$error.maxYear">Invalid max year</span>
 *             <br>
 *             <label for="minYearInput">Min year: </label>
 *             <input type="number" id="minYearInput" name="minYear" ng-model="minYear">
 *             <br>
 *             <label for="maxYearInput">Max year: </label>
 *             <input type="number" id="maxYearInput" name="maxYear" ng-model="maxYear">
 *             <br>
 *             <label>Enable validation: <input type="checkbox" ng-model="enableValidation"></label>
 *         </main>
 *     </file>
 *     <file name="script.js">
 *         angular.module('kpYearValidationExample', ['portaro.directives']);
 *     </file>
 * </example>
 * ```
 */
export default class KpYearValidationDirective {
    public static directiveName = 'kpYearValidation';

    private static minYearParam = 'minYear';
    private static maxYearParam = 'maxYear';

    public restrict = 'A';
    public require = 'ngModel';

    /*@ngInject*/
    constructor(private $parse: IParseService) {
    }

    public link($scope: IScope, $element, $attrs: IAttributes, ngModelController: INgModelController) {

        ngModelController.$validators.minYear = (modelValue, viewValue) => {
            const validationEnabled = this.$parse($attrs[KpYearValidationDirective.directiveName])($scope);

            if (validationEnabled === undefined || validationEnabled) {
                const minYear = Number.parseInt(this.$parse($attrs[KpYearValidationDirective.minYearParam])($scope), 10);

                if (ngModelController.$isEmpty(modelValue) || Number.isNaN(minYear)) {
                    return true;
                }
                return Number.parseInt(viewValue, 10) >= minYear;
            }

            return true;
        };

        ngModelController.$validators.maxYear = (modelValue, viewValue) => {
            const validationEnabled = this.$parse($attrs[KpYearValidationDirective.directiveName])($scope);

            if (validationEnabled === undefined || validationEnabled) {
                const maxYear = Number.parseInt(this.$parse($attrs[KpYearValidationDirective.maxYearParam])($scope), 10);

                if (ngModelController.$isEmpty(modelValue) || Number.isNaN(maxYear)) {
                    return true;
                }

                return Number.parseInt(viewValue, 10) <= maxYear;
            }

            return true;
        };

        $scope.$watch(() => [
                this.$parse($attrs[KpYearValidationDirective.directiveName])($scope),
                this.$parse($attrs[KpYearValidationDirective.minYearParam])($scope),
                this.$parse($attrs[KpYearValidationDirective.maxYearParam])($scope)
            ],
            () => ngModelController.$validate(),
            true);
    }
}
